import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Clock, Users, Baby, Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import useVoiceCommands from "../hooks/useVoiceCommands";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SpeechBubble, MillieAvatar, MillieHero, speak, prefetchSpeech, stopSpeaking } from "../components/MascotBubble";
import DishImage from "../components/DishImage";
import KidsFeedback from "../components/KidsFeedback";
import StepTimer, { extractTimers } from "../components/StepTimer";
import { LogLeftovers } from "../components/Leftovers";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const CHEERS = ["Too easy!", "You little ripper!", "Nailed it!", "Bonza — keep going!", "Smells good already!", "Nearly there, legend!"];

export default function CookMode() {
  const { day: dayParam, sosId } = useParams();
  const day = sosId ? "SOS" : dayParam === "today" ? DAY_NAMES[new Date().getDay()] : dayParam;
  const { activeId, activeHousehold } = useAuth();
  const navigate = useNavigate();
  const [meal, setMeal] = useState(null);
  const [error, setError] = useState(null);
  const [idx, setIdx] = useState(-1);

  const notifyStart = () => { api.post("/cook/steps", { household_id: activeId, day, sos_id: sosId || null, notify: true }).catch(() => {}); };

  const load = useCallback(async () => {
    if (!activeId) return;
    try { const { data } = await api.post("/cook/steps", { household_id: activeId, day, sos_id: sosId || null }); setMeal(data.meal); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
  }, [activeId, day, sosId]);

  useEffect(() => { load(); }, [load]);

  const steps = meal?.detailed_steps?.length ? meal.detailed_steps : meal?.steps || [];
  const done = idx >= steps.length;
  const kids = (activeHousehold?.members || []).filter((m) => m.role === "child" || (m.age_group && m.age_group !== "adult"));

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#E7F0EA] pb-10 flex flex-col">
        <div className="px-6 pt-12 flex items-center justify-between">
          <button onClick={() => navigate(-1)} data-testid="cook-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-3xl text-sorted-ink">Cook with Millie</h1>
          <span className="w-11" />
        </div>

        {error ? (
          <div className="px-6 mt-10 text-center">
            <p className="font-hand text-2xl text-sorted-ink">{error}</p>
            <button onClick={() => navigate("/meals")} className="mt-4 bg-sorted-rose text-white font-bold px-5 py-3 rounded-full">Back to my week</button>
          </div>
        ) : !meal ? (
          <div className="px-6 mt-8 flex flex-col items-center gap-4">
            <MillieAvatar size={72} mood="thinking" />
            <p className="font-hand text-2xl text-sorted-ink text-center animate-pulse">Getting the recipe ready for you…</p>
            <div className="w-full h-64 rounded-3xl bg-white/60 animate-pulse" />
          </div>
        ) : idx === -1 ? (
          <Intro meal={meal} day={day} steps={steps} onStart={() => { setIdx(0); notifyStart(); }} onKids={kids.length && !sosId ? () => navigate(`/cook/${dayParam}/kids`) : null} />
        ) : done ? (
          <Finish meal={meal} kids={kids} activeId={activeId} day={day} onDone={() => navigate("/home")} onWins={() => navigate("/wins")} />
        ) : (
          <Step steps={steps} idx={idx} setIdx={setIdx} meal={meal} />
        )}
      </div>
    </div>
  );
}

function Intro({ meal, day, steps, onStart, onKids }) {
  return (
    <div className="px-6 mt-4" data-testid="cook-intro">
      <DishImage meal={meal} className="w-full h-48 rounded-3xl" testId="cook-dish-image" />
      <p className="text-xs font-bold text-sorted-slate mt-4">{day === "SOS" ? "SOS rescue dinner" : `${day}'s dinner`}</p>
      <h2 className="font-heading text-2xl font-semibold text-sorted-ink" data-testid="cook-title">{meal.title}</h2>
      <div className="flex items-center gap-4 mt-2 text-sm font-bold text-sorted-slate">
        <span className="flex items-center gap-1"><Clock size={15} strokeWidth={2.5} />{meal.prep_time_min} min</span>
        <span className="flex items-center gap-1"><Users size={15} strokeWidth={2.5} />Serves {meal.servings}</span>
        <span>{steps.length} steps</span>
      </div>
      <div className="mt-4 bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft">
        <p className="font-heading font-semibold text-sorted-ink text-sm mb-2">Grab these first</p>
        <ul className="text-sm text-sorted-slate grid grid-cols-2 gap-x-3 gap-y-1">
          {meal.ingredients.map((ing, i) => <li key={i} className="truncate">• {ing.quantity} {ing.unit} {ing.name}</li>)}
        </ul>
      </div>
      <SpeechBubble className="mt-4" testId="cook-millie-intro">Right-o, aprons on! I'll take you through one step at a time — no rush, no stress.</SpeechBubble>
      <button onClick={onStart} data-testid="cook-start" className="mt-5 w-full bg-sorted-rose hover:bg-sorted-roseHover text-white font-bold py-4 rounded-full flex items-center justify-center gap-2">
        Let's cook <ArrowRight size={18} strokeWidth={2.5} />
      </button>
      {onKids && (
        <button onClick={onKids} data-testid="cook-kid-mode" className="mt-3 w-full bg-[#FFF3D6] border-2 border-white text-sorted-ink font-bold py-3 rounded-full flex items-center justify-center gap-2">
          <Baby size={18} strokeWidth={2.5} /> Little Chefs mode — kids can help
        </button>
      )}
    </div>
  );
}

const stepLine = (steps, i) => `${i === 0 ? "Right, step one." : `${CHEERS[i % CHEERS.length]} Step ${i + 1}.`} ${steps[i]}${i === steps.length - 1 ? " And that's dinner — you did it!" : ""}`;

function Step({ steps, idx, setIdx, meal }) {
  const cheer = CHEERS[idx % CHEERS.length];
  const timers = extractTimers(steps[idx]);
  const [voice, setVoice] = useState(() => localStorage.getItem("sorted_cook_voice") !== "off");
  const [talk, setTalk] = useState("idle");
  useEffect(() => {
    if (!voice) { stopSpeaking(); setTalk("idle"); return; }
    speak(stepLine(steps, idx), setTalk);
    if (idx + 1 < steps.length) prefetchSpeech(stepLine(steps, idx + 1));
  }, [idx, voice, steps]);
  useEffect(() => () => stopSpeaking(), []);
  const toggleVoice = () => { const v = !voice; setVoice(v); localStorage.setItem("sorted_cook_voice", v ? "on" : "off"); };

  const [handsFree, setHandsFree] = useState(() => localStorage.getItem("sorted_cook_handsfree") === "on");
  const onCommand = useCallback((cmd, text) => {
    if (cmd === "denied") { setHandsFree(false); localStorage.setItem("sorted_cook_handsfree", "off"); toast.error("Mic blocked — allow the microphone in your browser settings for hands-free"); return; }
    const say = (m) => toast(m, { duration: 1500, icon: "👂" });
    if (cmd === "next") { if (idx + 1 <= steps.length) { setIdx(idx + 1); say("Next step"); } }
    else if (cmd === "back") { if (idx > 0) { setIdx(idx - 1); say("Back a step"); } }
    else if (cmd === "repeat") { speak(stepLine(steps, idx), setTalk); say("Once more"); }
    else if (cmd === "timer_start" || cmd === "timer_pause") { window.dispatchEvent(new CustomEvent("sorted-timer", { detail: cmd })); say(cmd === "timer_start" ? "Timer started" : "Timer paused"); }
    else if (cmd === "hush") { stopSpeaking(); setTalk("idle"); say("Shh, got it"); }
  }, [idx, steps, setIdx]);
  const { listening, supported } = useVoiceCommands({ enabled: handsFree, paused: voice && talk !== "idle", onCommand });
  const toggleHandsFree = () => {
    if (!supported) return toast.error("Hands-free needs Safari or Chrome on your phone");
    const v = !handsFree; setHandsFree(v); localStorage.setItem("sorted_cook_handsfree", v ? "on" : "off");
    if (v) toast.success("Hands-free on — say \"next\", \"back\", \"repeat\" or \"start timer\"");
  };
  return (
    <div className="px-6 mt-4 flex-1 flex flex-col" data-testid="cook-step">
      <div className="flex gap-1.5">
        {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= idx ? "bg-sorted-rose" : "bg-[#FDECEF]"}`} />)}
      </div>
      <p className="text-xs font-bold text-sorted-slate mt-4">{meal.title}</p>
      <div className="flex items-center justify-between">
        <p className="font-hand text-3xl text-sorted-ink" data-testid="cook-step-counter">Step {idx + 1} of {steps.length}</p>
        <div className="flex items-center gap-1.5">
        <button onClick={toggleHandsFree} data-testid="cook-handsfree-toggle" data-handsfree={handsFree ? "on" : "off"} aria-label={handsFree ? "Turn off hands-free" : "Turn on hands-free"}
          className={`h-9 px-3 rounded-full border-2 flex items-center gap-1.5 text-xs font-bold ${handsFree ? "bg-sorted-ink border-sorted-ink text-white" : "bg-white border-[#FDECEF] text-sorted-slate"}`}>
          {handsFree ? <Mic size={14} strokeWidth={2.5} className={listening ? "animate-pulse text-sorted-rose" : ""} /> : <MicOff size={14} strokeWidth={2.5} />}{handsFree ? (listening ? "Listening" : "Hands-free") : "Hands-free"}
        </button>
        <button onClick={toggleVoice} data-testid="cook-voice-toggle" data-voice={voice ? "on" : "off"} aria-label={voice ? "Mute Millie" : "Unmute Millie"}
          className={`h-9 px-3 rounded-full border-2 flex items-center gap-1.5 text-xs font-bold ${voice ? "bg-sorted-peach border-white text-sorted-ink" : "bg-white border-[#FDECEF] text-sorted-slate"}`}>
          {voice ? <Volume2 size={14} strokeWidth={2.5} className={talk === "speaking" ? "animate-pulse" : ""} /> : <VolumeX size={14} strokeWidth={2.5} />}{voice ? (talk === "loading" ? "…" : talk === "speaking" ? "Millie's talking" : "Voice on") : "Muted"}
        </button>
        </div>
      </div>
      {handsFree && <p className="text-[11px] font-semibold text-sorted-slate mt-1" data-testid="cook-handsfree-hint">Say <b>"next"</b>, <b>"back"</b>, <b>"repeat"</b>, <b>"start timer"</b> or <b>"hush"</b>. I stop listening while I'm talking.</p>}
      <AnimatePresence mode="wait">
        <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.25 }}
          className="mt-4 bg-white rounded-3xl border-2 border-[#FDECEF] p-6 shadow-soft min-h-[200px] flex items-center">
          <p className="font-heading text-xl font-semibold text-sorted-ink leading-relaxed" data-testid="cook-step-text">{steps[idx]}</p>
        </motion.div>
      </AnimatePresence>
      {timers.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="cook-timers">
          {timers.map((t, i) => <StepTimer key={`${idx}-${i}`} label={t.label} secs={t.secs} testId={`cook-timer-${i}`} />)}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <MillieHero mood={idx === steps.length - 1 ? "celebrate" : "cooking"} entrance="none" className={`h-28 w-28 shrink-0 rounded-3xl transition-transform ${talk === "speaking" ? "scale-105" : ""}`} testId="cook-step-millie" />
        <button onClick={() => (talk === "speaking" ? (stopSpeaking(), setTalk("idle")) : speak(stepLine(steps, idx), setTalk))} data-testid="cook-step-replay" className="text-left">
          <p className="font-hand text-xl text-sorted-ink">{idx > 0 ? cheer : "Take your time, you've got this."}</p>
          <p className="text-[11px] font-bold text-sorted-slate">{talk === "speaking" ? "Tap to hush" : "Tap to hear this step again"}</p>
        </button>
      </div>
      <div className="mt-auto pt-6 flex items-center gap-3">
        <button onClick={() => setIdx(idx - 1)} disabled={idx === 0} data-testid="cook-prev" className="w-14 h-14 rounded-full bg-white border-2 border-[#FDECEF] text-sorted-ink flex items-center justify-center disabled:opacity-40"><ArrowLeft size={20} strokeWidth={2.5} /></button>
        <button onClick={() => setIdx(idx + 1)} data-testid="cook-next" className="flex-1 bg-sorted-rose hover:bg-sorted-roseHover text-white font-bold py-4 rounded-full flex items-center justify-center gap-2">
          {idx === steps.length - 1 ? <>Done <Check size={18} strokeWidth={2.5} /></> : <>Next step <ArrowRight size={18} strokeWidth={2.5} /></>}
        </button>
      </div>
    </div>
  );
}

function Finish({ meal, kids, activeId, day, onDone, onWins }) {
  return (
    <div className="px-6 mt-6" data-testid="cook-finish">
      <div className="text-center">
        <MillieHero mood="celebrate" entrance="wave" className="h-44 w-40 mx-auto" testId="cook-finish-millie" />
        <h2 className="font-hand text-4xl text-sorted-ink mt-2">Dinner's sorted!</h2>
        <p className="text-sorted-slate text-sm mt-1">{meal.title} — on the table. You bloody legend.</p>
      </div>
      <LogLeftovers activeId={activeId} mealTitle={meal.title} day={day} />
      {kids.length > 0 && (
        <div className="mt-6">
          <SpeechBubble testId="cook-kids-prompt">How did the kids go? Every taste counts — even a lick is a little win.</SpeechBubble>
          <div className="mt-3">
            <KidsFeedback kids={kids} activeId={activeId} food={meal.title} mealTitle={meal.title} />
          </div>
          <button onClick={onWins} data-testid="cook-view-wins" className="mt-3 w-full text-sm font-bold text-sorted-rose">See Little Wins →</button>
        </div>
      )}
      <button onClick={onDone} data-testid="cook-done" className="mt-6 w-full bg-sorted-rose text-white font-bold py-4 rounded-full">Back home</button>
    </div>
  );
}
